import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, FileText, GraduationCap, MessageCircle, PenLine } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import type { TutorAnalyticsSummary } from '../../../tutor/types/tutorAnalytics';
import type { FormativeHomeCard } from '../../tutor/api/malayFormativeProgress.client';
import type { AssignmentDashboardStats } from '../hooks/useAssignmentDashboardStats';
import {
  buildMalayRecentActivities,
  type MalayRecentActivityItem,
  type MalayRecentActivityType,
} from '../utils/malayRecentActivities';
import {
  DashboardCardSkeleton,
  DashboardEmptyState,
  DashboardInlineError,
} from './dashboardUi';

type Props = {
  formativeCards: FormativeHomeCard[];
  formativeLoading: boolean;
  formativeError: string | null;
  onRetryFormative?: () => void;
  tutorAnalytics: TutorAnalyticsSummary | null;
  tutorLoading: boolean;
  tutorError: string | null;
  tutorEnabled: boolean;
  genericTutorSessionLabel: string;
  onRetryTutor?: () => void;
  assignmentStats: AssignmentDashboardStats;
  assignmentLoading: boolean;
  assignmentError: string | null;
  assignmentEnabled: boolean;
  onRetryAssignment?: () => void;
  enabled: boolean;
};

function iconForType(type: MalayRecentActivityType) {
  switch (type) {
    case 'malay_unit':
    case 'tutor':
      return GraduationCap;
    case 'malay_reading':
      return BookOpen;
    case 'malay_narrative':
    case 'malay_narrative_draft':
      return PenLine;
    case 'homework':
      return FileText;
    default: {
      const _exhaustive: never = type;
      void _exhaustive;
      return MessageCircle;
    }
  }
}

function labelForType(type: MalayRecentActivityType, t: (k: string) => string): string {
  switch (type) {
    case 'malay_unit':
      return t('dashboard.studentHome.malayRecent.typeUnit');
    case 'malay_reading':
      return t('dashboard.studentHome.malayRecent.typeReading');
    case 'malay_narrative':
      return t('dashboard.studentHome.malayRecent.typeNarrative');
    case 'malay_narrative_draft':
      return t('dashboard.studentHome.malayRecent.typeNarrativeDraft');
    case 'tutor':
      return t('dashboard.studentHome.malayRecent.typeTutor');
    case 'homework':
      return t('dashboard.studentHome.malayRecent.typeAssignment');
    default: {
      const _exhaustive: never = type;
      void _exhaustive;
      return t('dashboard.studentHome.malayRecent.typeOther');
    }
  }
}

function formatWhen(d: Date, t: (k: string, opts?: Record<string, unknown>) => string): string {
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return t('dashboard.studentHome.analytics.today');
  if (diffDays === 1) return t('dashboard.studentHome.analytics.yesterday');
  if (diffDays < 7) return t('dashboard.studentHome.analytics.daysAgo', { n: diffDays });
  return d.toLocaleDateString();
}

export function MalayRecentActivityCard({
  formativeCards,
  formativeLoading,
  formativeError,
  onRetryFormative,
  tutorAnalytics,
  tutorLoading,
  tutorError,
  tutorEnabled,
  genericTutorSessionLabel,
  onRetryTutor,
  assignmentStats,
  assignmentLoading,
  assignmentError,
  assignmentEnabled,
  onRetryAssignment,
  enabled,
}: Props) {
  const { t } = useTranslation();

  const activities = useMemo(
    () =>
      buildMalayRecentActivities({
        formativeCards,
        tutorAnalytics,
        assignmentStats,
        genericTutorSessionLabel,
        t,
        maxItems: 4,
      }),
    [formativeCards, tutorAnalytics, assignmentStats, genericTutorSessionLabel, t],
  );

  const loading =
    formativeLoading ||
    (tutorEnabled && tutorLoading) ||
    (assignmentEnabled && assignmentLoading);

  const hasError =
    (formativeError && activities.length === 0) ||
    (tutorEnabled && tutorError && !tutorAnalytics && activities.length === 0) ||
    (assignmentEnabled && assignmentError && assignmentStats.total === 0 && activities.length === 0);

  if (!enabled) return null;

  if (loading) {
    return <DashboardCardSkeleton variant="list" />;
  }

  if (hasError) {
    return (
      <Card className="border-purple-200/60 dark:border-purple-500/25 mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-900 dark:text-white">
            {t('dashboard.studentHome.malayRecent.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DashboardInlineError
            message={
              formativeError
                ? t(formativeError)
                : tutorError ||
                  assignmentError ||
                  t('dashboard.studentHome.recentActivityError')
            }
            onRetry={formativeError ? onRetryFormative : tutorError ? onRetryTutor : onRetryAssignment}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200/60 dark:border-purple-500/25 mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-slate-900 dark:text-white">
          {t('dashboard.studentHome.malayRecent.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <DashboardEmptyState>
            {t('dashboard.studentHome.malayRecent.empty')}
          </DashboardEmptyState>
        ) : (
          <ul className="space-y-0 divide-y divide-slate-100 dark:divide-slate-700/80">
            {activities.map((item: MalayRecentActivityItem) => {
              const Icon = iconForType(item.type);
              return (
                <li key={item.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                    <Icon className="h-4 w-4" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {labelForType(item.type, t)}
                    </p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                      {item.title}
                    </p>
                  </div>
                  <time
                    className="shrink-0 text-xs text-slate-500 dark:text-slate-400"
                    dateTime={item.at.toISOString()}
                  >
                    {formatWhen(item.at, t)}
                  </time>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
